import _ from "lodash";

const parseLetter = (v: string | number): number => {
	if (Number(v)) return parseInt(String(v), 10);

	const value = String(v);
	if (value.length === 1) return value.charCodeAt(0);

	let sum = 0;
	for (let i = 0; i < value.length; i++) sum += value.charCodeAt(i);

	return sum;
};

const populate = (list: Array<number>, chunk: string) => {
	const ends: Array<string | number> = chunk.split("-");
	let inc = 1;

	if (ends.length === 1) {
		list.push(parseLetter(chunk));
		return;
	}

	if (!Number(ends[0])) {
		ends[0] = parseLetter(ends[0]);
		ends[1] = parseLetter(ends[1]);
	}

	if (/[e]$/.test(String(ends[1]))) {
		ends[1] = parseInt(String(ends[1]).slice(0, -1), 10);
		ends[0] = (ends[0] as number) & 1 ? parseInt(String(ends[0]), 10) + 1 : ends[0];
		inc = 2;
	} else if (/[o]$/.test(String(ends[1]))) {
		ends[1] = parseInt(String(ends[1]).slice(0, -1), 10);
		ends[0] = (ends[0] as number) & 1 ? ends[0] : parseInt(String(ends[0]), 10) + 1;
		inc = 2;
	}

	while (parseInt(String(ends[0]), 10) <= parseInt(String(ends[1]), 10)) {
		list.push(parseInt(String(+ends[0]), 10));
		ends[0] = parseInt(String(ends[0]), 10) + inc;
	}
};

const getNumbers = (str: string): Array<number> => {
	const out: Array<number> = [];
	const chunks = str.split(",");

	for (const chunk of chunks) populate(out, chunk);

	return out.sort((a, b) => a - b);
};

const concatLetter = (letter: string, times: number): string => {
	let newLetter = "";
	for (let i = 0; i < times; i++) newLetter += letter;
	return newLetter;
};

const getLetters = (words: Array<string>): Array<string> => {
	const letters = [...words];
	const ranges: Array<string> = [];

	while (letters.length !== 0) {
		const range = _.take(letters, 2);
		letters.shift();
		letters.shift();

		const first = _.first(range) ?? "";
		const last = _.last(range) ?? "";

		for (let i = first.charCodeAt(0); i <= last.charCodeAt(0); i++) {
			ranges.push(concatLetter(String.fromCharCode(i), first.length));
		}
	}

	return ranges;
};

interface ICompiledFilter {
	str: string;
	containsNumeric: boolean;
	even: boolean;
	odd: boolean;
	ranges: Array<string | number>;
	rangeSet: Set<string | number>;
}

const COMPILED_CACHE_LIMIT = 200;
const compiledCache = new Map<string, ICompiledFilter>();

const compileFilter = (str: string): ICompiledFilter => {
	const cached = compiledCache.get(str);
	if (cached) return cached;

	const collected: Array<string | number | Array<number>> = [];
	let ranges: Array<string | number> = [];

	const containsNumeric = /\d/.test(str);
	const strLetters = str.match(/[A-Z]/g);
	const even = strLetters != null ? (strLetters.length === 1 ? String(str.match(/[E]/g)) === "E" : false) : false;
	const odd = strLetters != null ? (strLetters.length === 1 ? String(str.match(/[O]/g)) === "O" : false) : false;

	if (containsNumeric) {
		_.each(str.split(" "), (rawChunk) => {
			const chunk = rawChunk.replace(/[A-Z]/g, "");
			if (chunk) collected.push(getNumbers(chunk));
		});
		ranges = _.flatten(collected);
	} else if (str.indexOf("-") > -1) {
		ranges = getLetters(_.words(str));
	} else {
		_.each(str.split(" "), (chunk) => collected.push(chunk));
		ranges = _.flatten(collected);
	}

	const compiled: ICompiledFilter = { str, containsNumeric, even, odd, ranges, rangeSet: new Set(ranges) };

	if (compiledCache.size >= COMPILED_CACHE_LIMIT) compiledCache.clear();
	compiledCache.set(str, compiled);

	return compiled;
};

export const filterComparer = (filter: string, candidate: string): boolean => {
	let sections: Array<string | number> = [];
	let pass = false;

	let value = _.trim((candidate ?? "").toString().toUpperCase());
	const compiled = compileFilter(_.trim((filter ?? "").toString().toUpperCase()));
	const { containsNumeric, even, odd, ranges, rangeSet } = compiled;

	let str = compiled.str;

	const piggyBack = !!(/\//.test(value) && containsNumeric);

	if (piggyBack && str.indexOf("-") === -1) {
		return _.isEqual(str.replace(/[' ]/g, "").split("/"), value.replace(/[' ]/g, "").split("/"));
	}

	if (_.includes(str, '"')) {
		str = str.replace(/["]/g, "");
		value = _.trim(value);

		if (_.includes(str, ",")) {
			pass = _.some(str.split(","), (o) => _.trim(o) === value);
		} else if (_.trim(str) === value) {
			pass = true;
		}
	} else if (str.indexOf("-") > -1) {
		if (even) str = str.replace(/[E]/g, "");
		if (odd) str = str.replace(/[O]/g, "");

		const chunks = _.words(value);

		if (containsNumeric) {
			_.each(chunks, (chunk) => {
				if (parseInt(chunk, 10)) {
					const numSection = parseInt(chunk, 10);
					if (even) {
						if (numSection % 2 === 0) sections.push(numSection);
					} else if (odd) {
						if (numSection % 2 !== 0) sections.push(numSection);
					} else {
						sections.push(numSection);
					}
				}
			});
		} else {
			_.each(chunks, (chunk) => {
				if (!parseInt(chunk, 10)) sections.push(chunk);
			});
		}
	} else {
		sections = [];
		const chunks = _.words(value);

		if (containsNumeric) {
			_.each(chunks, (rawChunk) => {
				const chunk = rawChunk.replace(/['A-Z']/g, "");
				if (parseInt(chunk, 10)) sections.push(parseInt(chunk, 10));
			});
		} else {
			_.each(chunks, (chunk) => {
				if (!parseInt(chunk, 10)) {
					_.each(ranges, (range) => {
						const exact = str.length === 1 ? chunk.length === String(range).length : true;
						if (_.includes(chunk, String(range)) && exact) pass = true;
					});
				}
			});
			return pass;
		}
	}

	_.each(sections, (section) => {
		if (rangeSet.has(section)) pass = true;
	});

	return pass;
};

const SINGLE_LETTER_RANGE = /^[A-Z]-[A-Z]$/;

export const expandFilterToken = (token: string): Array<string> => {
	const str = _.trim((token ?? "").toString().toUpperCase());
	if (!str) return [];

	if (_.includes(str, '"')) {
		return str
			.replace(/["]/g, "")
			.split(",")
			.map((part) => _.trim(part))
			.filter(Boolean);
	}

	const { containsNumeric, even, odd, ranges } = compileFilter(str);

	if (containsNumeric) {
		return ranges
			.filter((range) => {
				const value = Number(range);
				if (!Number.isFinite(value)) return false;
				if (even) return value % 2 === 0;
				if (odd) return value % 2 !== 0;
				return true;
			})
			.map((range) => String(range));
	}

	return SINGLE_LETTER_RANGE.test(str) ? ranges.map((range) => String(range)) : [];
};

const GENERAL_ADMISSION_WORD = /^(GA\d*|GEN|GENL|GENERAL|GENADM|GENERALADMISSION)$/;
const GENERAL_ADMISSION_QUERY = /^(GA|GEN|GENL|GENERAL|GENADM|GENERALADMISSION)$/;

const toWords = (text: string): Array<string> =>
	(text ?? "")
		.toUpperCase()
		.split(/[^A-Z0-9]+/)
		.filter(Boolean);

export const isGeneralAdmissionQuery = (token: string): boolean => {
	const words = toWords(token);
	return words.some((word) => GENERAL_ADMISSION_QUERY.test(word)) || GENERAL_ADMISSION_QUERY.test(words.join(""));
};

export const isGeneralAdmissionSection = (candidate: string): boolean => {
	const words = toWords(candidate);
	return words.some((word) => GENERAL_ADMISSION_WORD.test(word)) || GENERAL_ADMISSION_WORD.test(words.join(""));
};

const SHORT_ALPHA_TOKEN = /^[A-Z]{2,3}$/;

const PARKING_WORDS = ["PARK", "PARKING", "GARAGE", "GARAGES", "LOT", "LOTS", "VALET"];

const matchesGeneralAdmission = (token: string, candidate: string): boolean =>
	isGeneralAdmissionQuery(token) && isGeneralAdmissionSection(candidate);

const matchesShortToken = (token: string, candidate: string): boolean => {
	const wordPattern = new RegExp(`^${token}\\d*$`);

	return toWords(candidate).some((word) => wordPattern.test(word));
};

export const isParkingCandidate = (section: string, row?: string): boolean => {
	const rowWords = toWords(row ?? "");

	if (rowWords.some((word) => PARKING_WORDS.includes(word))) return true;
	if (rowWords.length > 0) return false;

	return toWords(section ?? "").some((word) => PARKING_WORDS.includes(word));
};

const isParkingIntentToken = (token: string): boolean => {
	const normalized = _.trim((token ?? "").toString().toUpperCase());

	if (_.includes(normalized, '"')) return true;

	return toWords(normalized).some((word) => PARKING_WORDS.includes(word));
};

export const matchesFilterToken = (token: string, candidate: string): boolean => {
	const normalizedToken = _.trim((token ?? "").toString().toUpperCase());

	if (SHORT_ALPHA_TOKEN.test(normalizedToken)) {
		return matchesShortToken(normalizedToken, candidate) || matchesGeneralAdmission(normalizedToken, candidate);
	}

	return filterComparer(token, candidate) || matchesGeneralAdmission(token, candidate);
};

export const parseFilterTokens = (input: string): Array<string> =>
	((input ?? "").toUpperCase().match(/"[^"]*"|[^\s,]+/g) ?? []).map((token) => token.trim()).filter(Boolean);

export const matchesFilterTokens = (tokens: Array<string>, candidate: string): boolean =>
	tokens.some((token) => matchesFilterToken(token, candidate));

export const matchesSectionFilterTokens = (tokens: Array<string>, section: string, row?: string): boolean => {
	if (!matchesFilterTokens(tokens, section)) return false;
	if (!isParkingCandidate(section, row)) return true;

	return tokens.some(isParkingIntentToken);
};
